import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Button, Badge, SectionHeader, Spinner, Modal } from '../../components/shared/UI';
import API from '../../utils/apiClient';

// TikTok oEmbed embed
function TikTokEmbed({ url }) {
  const [html, setHtml] = useState('');
  useEffect(() => {
    if (!url) return;
    fetch(`https://www.tiktok.com/oembed?url=${encodeURIComponent(url)}`)
      .then(r => r.json())
      .then(d => setHtml(d.html || ''))
      .catch(() => setHtml(''));
  }, [url]);
  if (!html) return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      className="text-sm text-brand-500 underline break-all">{url}</a>
  );
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}

const LEVELS    = ['A1','A2','B1','B2'];
const emptyForm = { title:'', instructions:'', level:'A1', due_date:'', max_score:100 };

export default function AdminSpeakingPage() {
  const { addToast } = useToast();
  const [exams, setExams]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(emptyForm);
  const [saving, setSaving]     = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [deleteModal, setDelete]= useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewingId, setViewing] = useState(null);
  const [subs, setSubs]         = useState([]);
  const [subsLoading, setSubsL] = useState(false);
  const [grades, setGrades]     = useState({});
  const [saving2, setSaving2]   = useState({});

  const load = () => {
    API.get('/speaking/exams')
      .then(({ data }) => setExams(data.exams || []))
      .catch(() => addToast('Failed to load', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleCreate = async () => {
    if (!form.title.trim() || !form.instructions.trim()) {
      addToast('Title and instructions required', 'error'); return;
    }
    setSaving(true);
    try {
      await API.post('/speaking/exams', form);
      addToast('Speaking exam created!', 'success');
      setForm(emptyForm);
      setShowForm(false);
      load();
    } catch (err) { addToast(err.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await API.delete(`/speaking/exams/${deleteModal.id}`);
      addToast('Deleted', 'success');
      setExams(p => p.filter(e => e.id !== deleteModal.id));
      setDelete(null);
    } catch (err) { addToast(err.message || 'Failed', 'error'); }
    finally { setDeleting(false); }
  };

  const openSubmissions = async (id) => {
    setViewing(id);
    setSubsL(true);
    try {
      const { data } = await API.get(`/speaking/exams/${id}/submissions`);
      const rows = data.submissions || [];
      setSubs(rows);
      const init = {};
      rows.forEach(s => { init[s.id] = { score: s.score ?? '', feedback: s.feedback || '' }; });
      setGrades(init);
    } catch { addToast('Failed to load submissions', 'error'); }
    finally { setSubsL(false); }
  };

  const saveGrade = async (subId) => {
    const g = grades[subId];
    if (g.score === '' || g.score === undefined) { addToast('Enter a score', 'error'); return; }
    setSaving2(p => ({ ...p, [subId]: true }));
    try {
      await API.post(`/speaking/submissions/${subId}/grade`, {
        score: parseInt(g.score), feedback: g.feedback,
      });
      addToast('Grade saved! Student notified by email.', 'success');
      setSubs(p => p.map(s => s.id === subId ? { ...s, score: parseInt(g.score), feedback: g.feedback } : s));
    } catch (err) { addToast(err.message || 'Failed', 'error'); }
    finally { setSaving2(p => ({ ...p, [subId]: false })); }
  };

  const viewExam = viewingId ? exams.find(e => e.id === viewingId) : null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SectionHeader title="Speaking Exams" subtitle="Assign and grade TikTok speaking tasks" />
        <div className="flex gap-2">
          {viewingId && (
            <Button variant="secondary" onClick={() => { setViewing(null); setSubs([]); }}>
              ← Back to Exams
            </Button>
          )}
          {!viewingId && (
            <Button variant="primary" onClick={() => setShowForm(p => !p)}>
              {showForm ? '× Cancel' : '+ New Speaking Exam'}
            </Button>
          )}
        </div>
      </div>

      {/* Create form */}
      {showForm && !viewingId && (
        <Card className="p-6" accent="red">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-bold text-de-black mb-1.5">Title *</label>
              <input type="text" value={form.title} onChange={e => setF('title', e.target.value)}
                placeholder="e.g. A1 — Introduce Yourself in German"
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-bold text-de-black mb-1.5">Instructions * <span className="text-xs font-normal text-gray-400">(shown to students)</span></label>
              <textarea value={form.instructions} onChange={e => setF('instructions', e.target.value)}
                rows={4}
                placeholder={`e.g. Record a 1-minute TikTok video introducing yourself in German.\nSay: your name, where you're from, your age, and one hobby.\nPost the video and submit the link below.`}
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none resize-none" />
            </div>

            <div>
              <label className="block text-sm font-bold text-de-black mb-1.5">Level</label>
              <select value={form.level} onChange={e => setF('level', e.target.value)}
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-white">
                {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-bold text-de-black mb-1.5">Due Date</label>
              <input type="datetime-local" value={form.due_date} onChange={e => setF('due_date', e.target.value)}
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
            </div>

            <div>
              <label className="block text-sm font-bold text-de-black mb-1.5">Max Score</label>
              <input type="number" min="1" max="1000" value={form.max_score}
                onChange={e => setF('max_score', parseInt(e.target.value))}
                className="w-full px-4 py-2.5 text-sm border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none" />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button variant="primary" loading={saving} onClick={handleCreate}>
              Create Speaking Exam
            </Button>
          </div>
        </Card>
      )}

      {/* Exams list */}
      {!viewingId && (
        <Card className="overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-10"><Spinner className="text-brand-500" /></div>
          ) : exams.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">No speaking exams yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-surface border-b border-gray-200">
                  <tr>
                    {['Title','Level','Due Date','Submissions','Actions'].map(h => (
                      <th key={h} className="text-left py-3 px-4 text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {exams.map(exam => (
                    <tr key={exam.id} className="border-b border-gray-50 hover:bg-surface transition-colors">
                      <td className="py-3 px-4">
                        <p className="font-semibold text-de-black text-xs">{exam.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{exam.instructions}</p>
                      </td>
                      <td className="py-3 px-4"><Badge variant="gold" size="xs">{exam.level}</Badge></td>
                      <td className="py-3 px-4 text-xs text-gray-500">
                        {exam.due_date
                          ? new Date(exam.due_date).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })
                          : 'No deadline'}
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-50 text-brand-600 rounded-full text-xs font-bold">
                          {exam.submission_count || 0} submitted
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <Button size="sm" variant="primary" onClick={() => openSubmissions(exam.id)}>
                            View Submissions
                          </Button>
                          <Button size="sm" variant="danger" onClick={() => setDelete(exam)}>
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Submissions view */}
      {viewingId && (
        <Card className="p-6">
          <div className="mb-5">
            <h3 className="text-base font-bold text-de-black">{viewExam?.title}</h3>
            <div className="mt-2 p-3 bg-surface rounded-xl border border-gray-200 text-xs text-de-text">
              {viewExam?.instructions}
            </div>
          </div>

          {subsLoading ? (
            <div className="flex justify-center py-8"><Spinner className="text-brand-500" /></div>
          ) : subs.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              <p className="text-2xl mb-2">🎤</p>
              No submissions yet.
            </div>
          ) : (
            <div className="space-y-6">
              {subs.map(sub => {
                const g = grades[sub.id] || { score: '', feedback: '' };
                return (
                  <div key={sub.id} className="border-2 border-gray-200 rounded-xl p-5 hover:border-gray-300 transition-colors">
                    {/* Student info */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-brand-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {sub.student_name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-de-black">{sub.student_name}</p>
                          <p className="text-xs text-gray-400">{sub.student_email}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {sub.score != null ? (
                          <Badge variant="completed" size="xs">Graded: {sub.score}/{viewExam?.max_score}</Badge>
                        ) : (
                          <Badge variant="gold" size="xs">Awaiting grade</Badge>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(sub.submitted_at).toLocaleDateString('en-GB', { day:'numeric', month:'short' })}
                        </p>
                      </div>
                    </div>

                    {/* TikTok embed */}
                    <div className="mb-4 flex justify-center">
                      <TikTokEmbed url={sub.tiktok_url} />
                    </div>

                    {/* Grading row */}
                    <div className="flex gap-3 items-end pt-4 border-t border-gray-100">
                      <div className="w-32">
                        <label className="block text-xs font-bold text-gray-600 mb-1">
                          Score (/{viewExam?.max_score})
                        </label>
                        <input type="number" min="0" max={viewExam?.max_score}
                          value={g.score}
                          onChange={e => setGrades(p => ({ ...p, [sub.id]: { ...g, score: e.target.value } }))}
                          className="w-full px-3 py-1.5 text-sm border-2 border-gray-200 rounded-lg focus:border-brand-500 focus:outline-none" />
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs font-bold text-gray-600 mb-1">Feedback for student</label>
                        <input type="text"
                          value={g.feedback}
                          placeholder="Great pronunciation! Work on your articles..."
                          onChange={e => setGrades(p => ({ ...p, [sub.id]: { ...g, feedback: e.target.value } }))}
                          className="w-full px-3 py-1.5 text-sm border-2 border-gray-200 rounded-lg focus:border-brand-500 focus:outline-none" />
                      </div>
                      <Button size="sm" variant="primary" loading={saving2[sub.id]}
                        onClick={() => saveGrade(sub.id)}>
                        💾 Save Grade
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* Delete confirm */}
      <Modal isOpen={!!deleteModal} onClose={() => setDelete(null)} title="Delete Speaking Exam" size="sm">
        {deleteModal && (
          <div>
            <p className="text-sm text-de-text mb-4">
              Delete <strong>"{deleteModal.title}"</strong>? All submissions will be permanently removed.
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="secondary" onClick={() => setDelete(null)}>Cancel</Button>
              <Button variant="danger" loading={deleting} onClick={handleDelete}>Delete</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
